import { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Check, Calendar } from 'lucide-react';

const revenueRanges = [
  'Under $1M',
  '$1M - $3M',
  '$3M - $5M',
  '$5M - $8M',
  '$8M - $15M',
  '$15M+',
];

const challenges = [
  'Revenue Stagnation or Decline',
  'Pre-Need Sales Development',
  'Operational Modernization',
  'Regulatory Compliance',
  'Board Governance Issues',
  'Exit or Transition Planning',
  'Technology Implementation',
  'Other',
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    revenueRange: '',
    challenge: '',
    phone: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  if (submitted) {
    return (
      <div>
        {/* Hero */}
        <section className="bg-slate-900 text-white py-12 md:py-16">
          <div className="container-custom">
            <div className="max-w-3xl">
              <h1 className="font-serif text-3xl md:text-4xl font-medium mb-4">
                Contact
              </h1>
              <p className="text-lg text-gray-300">
                Get in touch for experienced fractional COO leadership.
              </p>
            </div>
          </div>
        </section>

        {/* Success Message */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="max-w-lg mx-auto text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="font-serif text-xl font-medium text-slate-900 mb-3">
                Thank You for Your Interest
              </h2>
              <p className="text-slate-600 mb-6">
                Your message has been received. Todd Sexton will review your information 
                and respond within 2 business days to schedule a confidential consultation.
              </p>
              <p className="text-slate-500 text-sm">
                If your matter is urgent, please call directly at (555) 123-4567.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <h1 className="font-serif text-3xl md:text-4xl font-medium mb-4">
              Contact
            </h1>
            <p className="text-lg text-gray-300">
              Let's discuss your organization's next phase.
            </p>
          </div>
        </div>
      </section>
      {/* Contact Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-content mx-auto">
            {/* Contact Info */}
            <div className="lg:col-span-5">
              <h2 className="font-serif text-xl font-medium text-slate-900 mb-4">
                Let's Discuss Your Organization's Next Phase
              </h2>
              <p className="text-slate-600 mb-6 text-sm leading-relaxed">
                All discussions are confidential and structured around understanding fit and scope. 
                The objective is to determine fit, not to sell you into the wrong engagement.
              </p>

              {/* Calendar Scheduling */}
              <div className="mb-6 p-5 bg-accent-600 text-white rounded-sm">
                <Calendar className="w-7 h-7 text-accent-200 mb-2" />
                <h3 className="font-medium mb-1 text-sm">Schedule Directly</h3>
                <p className="text-accent-200 text-xs mb-3">
                  Prefer to book a time directly? Use our scheduling system.
                </p>
                <button className="text-white text-sm font-medium hover:text-accent-200 transition-colors">
                  View Available Times →
                </button>
              </div>

              <div className="space-y-4">
                
                
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-0.5 text-sm">Email</h3>
                    <p className="text-slate-600 text-sm">info@blackswan-consulting.com</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-slate-900 mb-0.5 text-sm">Location</h3>
                    <p className="text-slate-600 text-sm">Serving cemetery and deathcare organizations nationwide</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 border border-gray-100">
                <h3 className="font-medium text-slate-900 mb-1 text-sm">Response Commitment</h3>
                <p className="text-slate-600 text-xs">
                  All inquiries are treated with strict confidentiality. Expect a response 
                  within 2 business days.
                </p>
              </div>
            </div>

            {/* Calendly Widget */}
            <div className="lg:col-span-7">
              <div className="calendly-inline-widget" data-url="https://calendly.com/meeting-blackswan-consulting/todd-sexton" style={{minWidth: '320px', height: '700px'}}></div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
